import { z } from 'zod';

/** Actual authoring request budget, separate from the portable file/paste cap. */
export const BENCHMARK_AUTHORING_BODY_BYTES = 2 * 1024 * 1024;

/** Portable v1 authoring limits; API validators and browser consumers share these. */
export const BENCHMARK_INTERCHANGE_LIMITS = {
  bytes: 1024 * 1024,
  nameCodePoints: 255,
  longFieldCodePoints: 100_000,
  maxScore: 2_147_483_647,
  minCases: 1,
  maxCases: 20,
  minRepetitions: 1,
  maxRepetitions: 10,
} as const;

export const benchmarkCodePointLength = (text: string): number => Array.from(text).length;
export function isBenchmarkAuthoringText(
  value: unknown,
  maxCodePoints: number,
  required = false
): value is string {
  return (
    typeof value === 'string' &&
    !value.includes('\u0000') &&
    benchmarkCodePointLength(required ? value.trim() : value) <= maxCodePoints &&
    (!required || value.trim().length > 0)
  );
}

const authoringText = (maximum: number, required = false) =>
  z
    .string()
    .refine((value) => !value.includes('\u0000'), 'Remove U+0000; this character cannot be saved')
    .transform((value) => (required ? value.trim() : value))
    .refine(
      (value) => benchmarkCodePointLength(value) <= maximum,
      `Use at most ${maximum} Unicode code points`
    )
    .refine((value) => !required || value.length > 0, 'Enter a nonblank value');

export const BenchmarkInterchangeSchema = z
  .object({
    schemaVersion: z.literal(1),
    name: authoringText(BENCHMARK_INTERCHANGE_LIMITS.nameCodePoints, true),
    description: authoringText(BENCHMARK_INTERCHANGE_LIMITS.longFieldCodePoints).optional(),
    evaluationCriteria: authoringText(BENCHMARK_INTERCHANGE_LIMITS.longFieldCodePoints, true),
    scoringRubric: authoringText(BENCHMARK_INTERCHANGE_LIMITS.longFieldCodePoints).optional(),
    repetitionsPerCase: z.number().int().min(1).max(10).default(3),
    cases: z
      .array(
        z
          .object({
            inputPrompt: authoringText(BENCHMARK_INTERCHANGE_LIMITS.longFieldCodePoints, true),
            expectedOutput: authoringText(BENCHMARK_INTERCHANGE_LIMITS.longFieldCodePoints, true),
            maxScore: z
              .number()
              .int()
              .min(1)
              .max(BENCHMARK_INTERCHANGE_LIMITS.maxScore)
              .default(10),
          })
          .strict()
      )
      .min(1)
      .max(20),
  })
  .strict();

export type BenchmarkInterchangeV1 = z.output<typeof BenchmarkInterchangeSchema>;
export interface BenchmarkInterchangeError {
  path: string;
  message: string;
}
export type BenchmarkInterchangeResult =
  | { success: true; draft: BenchmarkInterchangeV1 }
  | { success: false; errors: BenchmarkInterchangeError[] };

const prototypeKeys = ['__proto__', 'prototype', 'constructor'];
function dangerousKeys(value: unknown, path = ''): BenchmarkInterchangeError[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return prototypeKeys
    .filter((key) => Object.prototype.hasOwnProperty.call(value, key))
    .map((key) => ({
      path: path ? `${path}.${key}` : key,
      message: 'Prototype keys are not authoring fields',
    }));
}

export function parseBenchmarkInterchange(text: string): BenchmarkInterchangeResult {
  if (typeof text !== 'string')
    return { success: false, errors: [{ path: '$', message: 'Supply JSON text' }] };
  if (new TextEncoder().encode(text).byteLength > BENCHMARK_INTERCHANGE_LIMITS.bytes)
    return {
      success: false,
      errors: [{ path: '$', message: 'JSON must be at most 1 MiB (1048576 UTF-8 bytes)' }],
    };
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return {
      success: false,
      errors: [{ path: '$', message: 'Invalid JSON; check quotes, commas and braces' }],
    };
  }
  const forbidden = dangerousKeys(value);
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'cases' in value &&
    Array.isArray(value.cases)
  )
    value.cases.forEach((item, index) => forbidden.push(...dangerousKeys(item, `cases.${index}`)));
  if (forbidden.length) return { success: false, errors: forbidden };
  const parsed = BenchmarkInterchangeSchema.safeParse(value);
  if (parsed.success) return { success: true, draft: parsed.data };
  return {
    success: false,
    errors: parsed.error.issues.flatMap((issue) => {
      const path = issue.path.join('.');
      return issue.code === 'unrecognized_keys'
        ? issue.keys.map((key) => ({
            path: path ? `${path}.${key}` : key,
            message: 'Unknown field; only portable authoring settings are accepted',
          }))
        : [{ path: path || '$', message: issue.message }];
    }),
  };
}

/** Deliberate allowlists: IDs, model runtime choices, source bytes and history never export. */
export function serializeBenchmarkInterchange(source: unknown): string {
  if (!source || typeof source !== 'object' || Array.isArray(source))
    throw new Error('A benchmark authoring definition is required');
  const selected: Record<string, unknown> = { schemaVersion: 1 };
  const object = source as Record<string, unknown>;
  for (const key of [
    'name',
    'description',
    'evaluationCriteria',
    'scoringRubric',
    'repetitionsPerCase',
  ]) {
    if (Object.prototype.hasOwnProperty.call(object, key) && object[key] !== undefined)
      selected[key] = object[key];
  }
  // Nullable saved descriptions represent omitted optional authoring text.
  if (selected.description === null) delete selected.description;
  if (Object.prototype.hasOwnProperty.call(object, 'cases'))
    selected.cases = Array.isArray(object.cases)
      ? object.cases.map((item: unknown) => {
          if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
          const record = item as Record<string, unknown>;
          const result: Record<string, unknown> = {};
          for (const key of ['inputPrompt', 'expectedOutput', 'maxScore'])
            if (Object.prototype.hasOwnProperty.call(record, key) && record[key] !== undefined)
              result[key] = record[key];
          return result;
        })
      : object.cases;
  const result = parseBenchmarkInterchange(JSON.stringify(selected));
  if (!result.success)
    throw new Error(result.errors.map((error) => `${error.path}: ${error.message}`).join('; '));
  const pretty = JSON.stringify(result.draft, null, 2);
  if (new TextEncoder().encode(pretty).byteLength <= BENCHMARK_INTERCHANGE_LIMITS.bytes)
    return pretty;
  const compact = JSON.stringify(result.draft);
  if (new TextEncoder().encode(compact).byteLength <= BENCHMARK_INTERCHANGE_LIMITS.bytes)
    return compact;
  throw new Error('Export exceeds the portable 1 MiB UTF-8 limit');
}
